import { inject, Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { PostTradingEventInterface } from '@modules/options-module/post-trading/interfaces/post-trading-event.interface'
import { PostTradingEventTypeEnum } from '@modules/options-module/post-trading/enums/post-trading-event-type.enum'
import { PostTradingEventQueryParamsInterface } from '@modules/options-module/post-trading/interfaces/post-trading-event-query-params.interface'
import { TuiDay, TuiDayRange } from '@taiga-ui/cdk'
import { ParsedOptionSymbolInterface } from '@modules/options-module/general/interfaces/parsed-option-symbol.interface'
import { OptionsHelperService } from '@modules/options-module/general/services/options-helper.service'

@Injectable({
	providedIn: 'root',
})
export class PostTradingHelperService {
	protected readonly tuiDay: typeof TuiDay = TuiDay
	protected readonly map: MapConstructor = Map
	protected readonly date: DateConstructor = Date
	protected readonly postTradingEventTypeEnum: typeof PostTradingEventTypeEnum = PostTradingEventTypeEnum

	public postTradingEventsMap: Map<PostTradingEventTypeEnum, BehaviorSubject<PostTradingEventInterface[]>> =
		new this.map<PostTradingEventTypeEnum, BehaviorSubject<PostTradingEventInterface[]>>([
			[this.postTradingEventTypeEnum.EXPIRED, new BehaviorSubject<PostTradingEventInterface[]>([])],
			[this.postTradingEventTypeEnum.EXERCISES, new BehaviorSubject<PostTradingEventInterface[]>([])],
			[this.postTradingEventTypeEnum.ASSIGNED, new BehaviorSubject<PostTradingEventInterface[]>([])],
		])

	public postTradingEventsLoading: Map<PostTradingEventTypeEnum, BehaviorSubject<boolean>> = new this.map<
		PostTradingEventTypeEnum,
		BehaviorSubject<boolean>
	>([
		[this.postTradingEventTypeEnum.EXPIRED, new BehaviorSubject<boolean>(false)],
		[this.postTradingEventTypeEnum.EXERCISES, new BehaviorSubject<boolean>(false)],
		[this.postTradingEventTypeEnum.ASSIGNED, new BehaviorSubject<boolean>(false)],
	])

	private readonly _optionsHelperService: OptionsHelperService = inject<OptionsHelperService>(OptionsHelperService)

	private readonly daysOffset: number = 10

	private _calcDateFromTo(inputDate: TuiDay, type: 'from' | 'to', reverse: boolean = false): string {
		const date: Date = new this.date(this.date.UTC(inputDate.year, inputDate.month, inputDate.day))

		const offset: number = this.daysOffset * (reverse ? -1 : 1)
		const daysToAdd: number = type === 'from' ? -offset : offset

		date.setDate(date.getDate() + daysToAdd)

		return date.toISOString().slice(0, 10)
	}

	public getPostTradingEventsByType$(
		type: PostTradingEventTypeEnum,
	): BehaviorSubject<PostTradingEventInterface[]> | undefined {
		return this.postTradingEventsMap.get(type)
	}

	public getPostTradingEventsLoadingByType$(type: PostTradingEventTypeEnum): BehaviorSubject<boolean> | undefined {
		return this.postTradingEventsLoading.get(type)
	}

	public getPostTradingEventsParams(
		type: PostTradingEventTypeEnum,
		ticker: string,
		datesRange: TuiDayRange | null,
	): PostTradingEventQueryParamsInterface {
		const params: PostTradingEventQueryParamsInterface = {
			entry_type: type,
			date_from: '',
			date_to: '',
		}

		if (ticker.trim().length) params.ticker = ticker.trim()

		if (datesRange?.from) {
			params.date_from = this._calcDateFromTo(datesRange.from, 'from')
		}

		if (datesRange?.to) {
			params.date_to = this._calcDateFromTo(datesRange.to, 'to')
		}

		return params
	}

	public setPostTradingEventsByType(
		postTradingEvents: PostTradingEventInterface[],
		type: PostTradingEventTypeEnum,
	): void {
		const postTradingEvents$: BehaviorSubject<PostTradingEventInterface[]> | undefined =
			this.getPostTradingEventsByType$(type)
		if (!postTradingEvents$) return

		postTradingEvents$.next(this.formatPostTradingEvents(postTradingEvents))
	}

	public setLoadingByState(type: PostTradingEventTypeEnum, value: boolean): void {
		this.postTradingEventsLoading.get(type)?.next(value)
	}

	public formatPostTradingEvents(postTradingEvents: PostTradingEventInterface[]): PostTradingEventInterface[] {
		return postTradingEvents.map((postTradingEvent: PostTradingEventInterface): PostTradingEventInterface => {
			const parsedOptionSymbol: ParsedOptionSymbolInterface | null = this._optionsHelperService.parseOptionSymbol(
				postTradingEvent.option_symbol,
			)
			if (!parsedOptionSymbol) return postTradingEvent

			return {
				...postTradingEvent,
				...parsedOptionSymbol,
			}
		})
	}

	public filterPostTradingEvents(
		postTradingEvents: PostTradingEventInterface[],
		dateFrom: string,
		dateTo: string,
	): PostTradingEventInterface[] {
		const fromDay: TuiDay = this.tuiDay.fromLocalNativeDate(new this.date(dateFrom))
		const toDay: TuiDay = this.tuiDay.fromLocalNativeDate(new this.date(dateTo))

		const start: Date = new this.date(this._calcDateFromTo(fromDay, 'from', true))
		const end: Date = new this.date(this._calcDateFromTo(toDay, 'to', true))

		return postTradingEvents.filter((option: PostTradingEventInterface): boolean => {
			const settle: Date = new this.date(option.settle_date)
			return settle >= start && settle <= end
		})
	}
}
